import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import './pages.css';

interface RefinementFile {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

const Refinement: React.FC = () => {
  const [inputType, setInputType] = useState<'file' | 'text'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [clientRf, setClientRf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<RefinementFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [loadingFiles, setLoadingFiles] = useState(false);

  const loadAvailableFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const response = await apiClient.get('/refinement/files/');
      setAvailableFiles(response.data.files || []);
    } catch (err: any) {
      console.error('Error loading files:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadAvailableFiles();
  }, [loadAvailableFiles]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError(null);
      setSuccess(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (inputType === 'file') {
      if (!file) {
        setError('Please select a file');
        return;
      }
    }

    if (inputType === 'text' && !clientRf.trim()) {
      setError('Please enter Client RF text');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    const formData = new FormData();

    if (inputType === 'file') {
      formData.append('file', file!);
      formData.append('input_type', 'file');
    } else {
      formData.append('input_type', 'text');
      formData.append('client_rf', clientRf);
    }

    try {
      await apiClient.post('/refinement/refine/', formData);
      
      // Reload available files after successful refinement
      await loadAvailableFiles();
      
      setSuccess(true);
    } catch (err: any) {
      console.error('Refinement error:', err);
      const errorData = err.response?.data;
      const statusCode = err.response?.status;

      // Build a detailed error message
      let errorMessage = 'An error occurred during refinement';

      // Handle 413 error (file too large) - can come from nginx or backend
      const is413Error = statusCode === 413 ||
        err.message?.includes('413') ||
        (typeof errorData === 'string' && errorData.includes('413'));

      if (is413Error) {
        errorMessage = 'The file you selected is too large to upload. The maximum file size is 300 MB.  Please try with a smaller file or reduce the file size before uploading.';
      } else if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) {
      setError('Please select a file to download');
      return;
    }

    try {
      const response = await apiClient.get(`/refinement/files/${selectedFile}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 
        (err.response?.status === 404 
          ? `File "${selectedFile}" not found. It may have been deleted.`
          : 'An error occurred while downloading the file. Please try again.');
      setError(errorMessage);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="page-container">
      <h1 className="page-title">URS Refinement Tool</h1>
      <div className="page-description">
        <p>
          This tool enables you to refine requirements using LLMs, with support for context-aware improvements and guideline enforcement.
        </p>
        <h3>How to Use:</h3>
        <ol>
          <li><strong>Select</strong> the input type: "URS file" or "Client RF".</li>
          <li>If you have an <strong>URS file</strong>, upload an Excel file.</li>
          <li>If you have a <strong>Client RF</strong>, type it in the text area.</li>
          <li><strong>Click Execute Refinement</strong> to run the refinement.</li>
          <li><strong>Select and download</strong> the generated Excel file from the list below.</li>
        </ol>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="upload-section">
          <label className="upload-label">Choose the input type:</label>
          <div className="margin-top-small">
            <label className="radio-label">
              <input
                type="radio"
                value="file"
                checked={inputType === 'file'}
                onChange={(e) => setInputType(e.target.value as 'file' | 'text')}
                disabled={loading}
              />
              {' '}URS file
            </label>
            <label>
              <input
                type="radio"
                value="text"
                checked={inputType === 'text'}
                onChange={(e) => setInputType(e.target.value as 'file' | 'text')}
                disabled={loading}
              />
              {' '}Client RF
            </label>
          </div>
        </div>

        {inputType === 'file' ? (
          <div className="upload-section">
            <label className="upload-label" htmlFor="file">
              Choose an Excel file
            </label>
            <input
              id="file"
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="file-input"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="upload-section">
            <label className="upload-label" htmlFor="clientRf">
              Enter Client RF
            </label>
            <textarea
              id="clientRf"
              value={clientRf}
              onChange={(e) => setClientRf(e.target.value)}
              className="file-input"
              rows={10}
              placeholder="Enter your Client RF here..."
              disabled={loading}
            />
          </div>
        )}

        {error && (
          <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
            {error}
          </div>
        )}
        {success && <div className="success-message">Refinement completed successfully! Select a file from the list below to download.</div>}

        <button
          type="submit"
          className="button"
          disabled={
            loading ||
            (inputType === 'file' ? !file : !clientRf.trim())
          }
        >
          {loading ? 'Processing...' : 'Execute Refinement'}
        </button>
      </form>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      <div className="results-section">
        <h2 className="results-title">Available Refinement Results</h2>
        
        {loadingFiles ? (
          <p>Loading files...</p>
        ) : availableFiles.length === 0 ? (
          <p className="text-secondary">No refinement results available yet.</p>
        ) : (
          <>
            <div className="upload-section">
              <label className="upload-label" htmlFor="fileSelect">
                Select a file to download
              </label>
              <select
                id="fileSelect"
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                className="file-input select-margin-bottom"
              >
                <option value="">Select a file...</option>
                {availableFiles.map((file) => (
                  <option key={file.filename} value={file.filename}>
                    {file.filename} ({formatFileSize(file.size)}) - {formatDate(file.modified)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="button"
              onClick={handleDownload}
              disabled={!selectedFile}
            >
              Download Selected File
            </button>

            <button
              type="button"
              className="button button-refresh"
              onClick={loadAvailableFiles}
            >
              Refresh List
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Refinement;

